// localStorage-backed "recently used" / "most used" tool tracking for the
// header search's empty-query panel. Every access is wrapped in try/catch —
// localStorage can throw (private browsing, blocked storage, quota) and the
// header must keep working with just an empty panel if it does.

const RECENT_KEY = 'devstackkit_recent_tools';
const USAGE_KEY = 'devstackkit_tool_usage_counts';

const MAX_RECENT = 8;
const MAX_MOST_USED = 5;
export const TOOL_USAGE_UPDATED_EVENT = 'devstackkit:tool-usage-updated';

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Blocked/full storage — silently skip. Recording a visit is a nice-to-have,
    // never something the rest of the page should fail over.
  }
}

// Called once per real tool-page mount (see ToolLayout.tsx) — updates both
// the recent list (deduped, most-recent-first, capped) and the usage-count
// map in one localStorage round trip each.
export function recordToolVisit(toolId: string): void {
  if (!toolId) return;

  const recent = readJson<string[]>(RECENT_KEY, []);
  const nextRecent = [toolId, ...recent.filter((id) => id !== toolId)].slice(0, MAX_RECENT);
  writeJson(RECENT_KEY, nextRecent);

  const counts = readJson<Record<string, number>>(USAGE_KEY, {});
  counts[toolId] = (counts[toolId] ?? 0) + 1;
  writeJson(USAGE_KEY, counts);
  window.dispatchEvent(new Event(TOOL_USAGE_UPDATED_EVENT));
}

export function getRecentTools(): string[] {
  return readJson<string[]>(RECENT_KEY, []).slice(0, MAX_RECENT);
}

export function getMostUsedTools(): string[] {
  const counts = readJson<Record<string, number>>(USAGE_KEY, {});
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, MAX_MOST_USED)
    .map(([id]) => id);
}

export function getToolUsageCount(): number {
  const counts = readJson<Record<string, number>>(USAGE_KEY, {});
  return Object.values(counts).reduce((sum, count) => sum + count, 0);
}
